import {
	Alert,
	Box,
	Card,
	CardContent,
	Chip,
	Divider,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import { Title } from 'react-admin';

const Code = ({ children }) => (
	<Box
		component="code"
		sx={{
			fontFamily: 'monospace',
			fontSize: '0.85em',
			px: 0.6,
			py: 0.1,
			borderRadius: 1,
			bgcolor: 'action.selected',
			whiteSpace: 'nowrap',
		}}
	>
		{children}
	</Box>
);

const Section = ({ id, title, roles, children }) => (
	<Card id={id} sx={{ mb: 3, scrollMarginTop: '90px' }}>
		<CardContent>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
				<Typography variant="h5" component="h2">
					{title}
				</Typography>
				{roles && <Chip size="small" label={roles} color="primary" variant="outlined" />}
			</Box>
			<Divider sx={{ mb: 2 }} />
			{children}
		</CardContent>
	</Card>
);

const P = ({ children, ...props }) => (
	<Typography variant="body1" sx={{ mb: 1.5 }} {...props}>
		{children}
	</Typography>
);

const H3 = ({ children }) => (
	<Typography variant="h6" component="h3" sx={{ mt: 2.5, mb: 1 }}>
		{children}
	</Typography>
);

const UL = ({ children }) => (
	<Box component="ul" sx={{ mt: 0, mb: 1.5, pl: 3, '& li': { mb: 0.75 } }}>
		{children}
	</Box>
);

const OL = ({ children }) => (
	<Box component="ol" sx={{ mt: 0, mb: 1.5, pl: 3, '& li': { mb: 0.75 } }}>
		{children}
	</Box>
);

const RefTable = ({ head, rows }) => (
	<Table size="small" sx={{ mb: 2, '& th': { fontWeight: 600 } }}>
		<TableHead>
			<TableRow>
				{head.map((h) => (
					<TableCell key={h}>{h}</TableCell>
				))}
			</TableRow>
		</TableHead>
		<TableBody>
			{rows.map((row, i) => (
				<TableRow key={i}>
					{row.map((cell, j) => (
						<TableCell key={j}>{cell}</TableCell>
					))}
				</TableRow>
			))}
		</TableBody>
	</Table>
);

const TOC_ITEMS = [
	['getting-started', 'Getting Started'],
	['dashboard', 'Dashboard'],
	['sessions', 'Sessions'],
	['modules', 'Modules & Prompts'],
	['prompt-templates', 'Prompt Templates'],
	['prompt-preview', 'Prompt Preview'],
	['conversations', 'Conversations'],
	['groups', 'Groups'],
	['users', 'Users & Import'],
	['ip-access', 'IP Access'],
	['tips', 'Tips & Troubleshooting'],
];

const scrollTo = (id) => {
	const el = document.getElementById(id);
	if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export const Help = () => (
	<Box sx={{ maxWidth: 980, mx: 'auto', pb: 6 }}>
		<Title title="Help" />

		<Card sx={{ mb: 3 }}>
			<CardContent>
				<Typography variant="h4" component="h1" gutterBottom>
					Admin Guide
				</Typography>
				<P color="text.secondary">
					How to manage sessions, modules, prompts, users, and access in the PCE CMS.
					Some areas are limited by role — a chip on each section shows who can use it.
				</P>
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
					{TOC_ITEMS.map(([id, label]) => (
						<Chip key={id} label={label} onClick={() => scrollTo(id)} clickable />
					))}
				</Box>
			</CardContent>
		</Card>

		<Section id="getting-started" title="Getting Started">
			<P>
				Sign in with your email address and password. If your account has been
				deactivated or your IP address is blocked, you will not be able to log in —
				contact an administrator.
			</P>
			<H3>Roles</H3>
			<RefTable
				head={['Role', 'What they can do']}
				rows={[
					['Super Admin', 'Everything, including Prompt Templates and Prompt Preview.'],
					['Admin', 'Everything except Prompt Templates and Prompt Preview.'],
					['App Admin', 'Dashboard, Sessions, and Conversations; can view (not edit) Modules.'],
					['Event Manager', 'View Sessions only.'],
					['User', 'No CMS access (end-user account for the kiosk experience).'],
				]}
			/>
			<UL>
				<li>Menu items you cannot access are hidden automatically.</li>
				<li>The theme toggle in the top bar switches between dark (default) and light mode.</li>
				<li>
					After a new version of the CMS is deployed, a banner will prompt you to reload
					the page.
				</li>
			</UL>
		</Section>

		<Section id="dashboard" title="Dashboard" roles="Super Admin · Admin · App Admin">
			<P>
				The Dashboard charts session activity. It loads with the last week of data;
				use the filter bar to change what is shown, then click <strong>Filter</strong>.
			</P>
			<UL>
				<li>
					<strong>Session(s)</strong> — limit the chart to specific sessions
					(leave blank for all).
				</li>
				<li>
					<strong>Date range</strong> — Today, Yesterday, Last week, Last 30 days,
					Last Month, or Custom (which reveals start/end date pickers).
				</li>
				<li>
					The chart shows <strong>Sessions Completed</strong>,{' '}
					<strong>Sessions Abandoned</strong> (started but never finished), and{' '}
					<strong>Time Engaged</strong> (average duration per session). Totals appear
					beside the chart.
				</li>
				<li>
					<strong>Export</strong> downloads a CSV of daily totals for the selected
					filters. Today&apos;s partial day is excluded from aggregates.
				</li>
			</UL>
		</Section>

		<Section id="sessions" title="Sessions" roles="Super Admin · Admin · App Admin">
			<P>
				A Session is an event that end users join in the kiosk. The list shows each
				session&apos;s name, start date, and an <strong>Active</strong> toggle — flip it to
				activate or deactivate a session instantly without opening the edit form.
			</P>
			<H3>Creating or editing a session</H3>
			<UL>
				<li>
					<strong>Name</strong> (required) — internal name used in the CMS.
				</li>
				<li>
					<strong>Public Session Name</strong> (required) — the name end users see in
					the kiosk.
				</li>
				<li>
					<strong>Persona</strong> (required) — the AI voice/personality for the
					session: Ashley, Emma, Nova, Andrew, or Davis.
				</li>
				<li>
					<strong>Start Date</strong> — when the event takes place.
				</li>
				<li>
					<strong>Session Visibility</strong> — <em>All Users</em> makes the session
					available to everyone; <em>Specific Groups</em> reveals a{' '}
					<strong>Groups</strong> picker so only members of those groups can join.
				</li>
			</UL>
			<H3>Attaching modules</H3>
			<P>
				The <strong>Modules</strong> section at the bottom of the form controls which
				conversation modules run in this session and in what order.
			</P>
			<UL>
				<li>
					Click <strong>+</strong> to add a module tab, then pick a module from the
					dropdown. <strong>Drag the tabs</strong> left or right to reorder — the tab
					order is the order users experience.
				</li>
				<li>
					Each tab has its own <strong>Active</strong> switch, an optional{' '}
					<strong>Label</strong> (overrides the module title for this session),{' '}
					<strong>Conversation Context</strong>, and <strong>Persona Context</strong> —
					free-text instructions layered into the prompts for this session only.
				</li>
				<li>
					A <strong>green dot</strong> on a tab means the module is active in this
					session; a grey dot means inactive. A <strong>warning triangle</strong> means
					the module itself is globally inactive and will not appear for users even if
					it is active here.
				</li>
				<li>
					<strong>Remove Module</strong> detaches the module from the session (the
					module itself is not deleted). <strong>Cancel Changes</strong> reverts one
					tab to its last saved state.
				</li>
			</UL>
			<Alert severity="info" sx={{ mb: 1 }}>
				Module changes are not saved until you click <strong>Save</strong> on the
				session form.
			</Alert>
		</Section>

		<Section id="modules" title="Modules & Module Prompts" roles="Super Admin · Admin (App Admin: view only)">
			<P>
				A Module is a reusable conversation unit — for example a negotiation scenario —
				that can be attached to any number of sessions. The list shows title,
				description, and a live <strong>Active</strong> toggle, plus a{' '}
				<strong>Clone</strong> button per row.
			</P>
			<UL>
				<li>
					<strong>Title</strong> (required), <strong>Description</strong>, and{' '}
					<strong>Active</strong>. Inactive modules never appear to end users, even if
					attached to active sessions.
				</li>
				<li>
					<strong>Clone Module</strong> copies the module and its currently active
					prompts into a new module and opens it for editing. The clone keeps the same
					title — rename it so you can tell them apart.
				</li>
			</UL>
			<H3>The Prompts editor</H3>
			<P>
				Each module carries three prompts, edited on the <strong>Start</strong>,{' '}
				<strong>Interview</strong>, and <strong>Summary</strong> tabs. These are injected
				into the global Prompt Templates at runtime — Start opens the conversation,
				Interview drives it, and Summary generates the user&apos;s feedback.
			</P>
			<P>
				Prompts are <strong>versioned</strong>. Every version is a draft, active, or
				archived:
			</P>
			<UL>
				<li>
					Versions open in view mode. Click <strong>Edit</strong> on an active prompt
					or draft to change its text. <strong>Save Prompt</strong> updates
					an active prompt immediately; <strong>Cancel</strong> discards edits and returns to view mode.
				</li>
				<li>
					<strong>Copy to New</strong> creates a fresh draft from the displayed prompt,
					including any unsaved text, even if other drafts already exist. The new draft
					opens in view mode; click <strong>Edit</strong> to change it. Select existing
					drafts from the version dropdown.
				</li>
				<li>
					Use <strong>Save Draft</strong> as you work. Drafts have no effect on the live
					experience.
				</li>
				<li>
					<strong>Activate Version</strong> makes the selected version live and
					automatically archives whichever version was active before. Exactly one
					version per stage can be active.
				</li>
				<li>
					The <strong>Select version</strong> dropdown switches between versions
					(newest first). Switching away from unsaved prompt edits will ask you to
					confirm discarding them.
				</li>
				<li>
					Select an inactive draft or archived version and click <strong>Delete</strong>{' '}
					to permanently remove it after confirmation. Active versions cannot be deleted.
				</li>
				<li>
					If the global template for a stage includes authoring guidance, it appears in
					a read-only <strong>Module prompt guidance</strong> panel beside the editor —
					follow it when writing the module prompt.
				</li>
				<li>
					Super Admins also see <strong>Preview</strong> (renders the draft inside the
					global template without saving) and a link to edit or view the stage&apos;s
					global template.
				</li>
			</UL>
			<Alert severity="warning" sx={{ mb: 1 }}>
				A module must have an <strong>active</strong> prompt version for each stage
				(Start, Interview, Summary). If a stage has no active version, conversations
				using that module will fail.
			</Alert>
		</Section>

		<Section id="prompt-templates" title="Prompt Templates" roles="Super Admin only">
			<P>
				Prompt Templates are the global system-prompt skeletons shared by every module.
				There is one template slot per stage — <strong>Start</strong>,{' '}
				<strong>Interview</strong>, <strong>Summary</strong>, and{' '}
				<strong>Analyze Transcript</strong> — shown as tabs. Templates use the same
				draft → activate versioning as module prompts: click <strong>Edit</strong> on a
				read-only version to create a draft, <strong>Save Draft</strong> while working,
				and <strong>Activate Version</strong> to make it live (archiving the previous
				active version).
			</P>
			<UL>
				<li>
					<strong>Version Label</strong> and <strong>Description / Internal Notes</strong>{' '}
					are for your own bookkeeping — they are never sent to the AI.
				</li>
				<li>
					<strong>Module prompt guidance</strong> is shown to module authors beside
					their prompt editor. Use it to explain what the module prompt for this stage
					should contain.
				</li>
				<li>
					The template <strong>body</strong> contains placeholder tokens that are
					replaced at runtime. Unknown or unresolved placeholders are left in the text
					verbatim — use Prompt Preview to check for them before activating.
				</li>
			</UL>
			<H3>Placeholder reference</H3>
			<RefTable
				head={['Placeholder', 'Replaced with']}
				rows={[
					[<Code>{'{{PERSONA}}'}</Code>, 'The full persona prompt text'],
					[<Code>{'{{PERSONA_NAME}}'}</Code>, 'The persona display name'],
					[<Code>{'{{CONSULTANT_NAME}}'}</Code>, "The participant's name"],
					[<Code>{'{{CONSULTANT_ROLE}}'}</Code>, "The participant's role"],
					[<Code>{'{{MODULE_TITLE}}'}</Code>, 'The module title'],
					[<Code>{'{{MODULE_DESCRIPTION}}'}</Code>, 'The module description'],
					[
						<Code>{'{{MODULE_CONVERSATION_CONTEXT}}'}</Code>,
						'The session-module Conversation Context field',
					],
					[
						<Code>{'{{MODULE_PERSONA_CONTEXT}}'}</Code>,
						'The session-module Persona Context field',
					],
					[
						<Code>{'{{MODULE_START_PROMPT}}'}</Code>,
						"The module's active Start prompt (Start stage only)",
					],
					[
						<Code>{'{{MODULE_INTERVIEW_PROMPT}}'}</Code>,
						"The module's active Interview prompt",
					],
					[
						<Code>{'{{MODULE_SUMMARY_PROMPT}}'}</Code>,
						"The module's active Summary prompt (Summary stage only)",
					],
					[
						<Code>{'{{INTERVIEW_TRANSCRIPT}}'}</Code>,
						'The conversation transcript (Summary stage only)',
					],
					[<Code>{'{{SESSION_START_TIME}}'}</Code>, 'The session start timestamp'],
					[<Code>{'{{ROLE_DETAILS}}'}</Code>, "Level-specific role details, if the participant has a level"],
				]}
			/>
		</Section>

		<Section id="prompt-preview" title="Prompt Preview" roles="Super Admin only">
			<P>
				Prompt Preview renders the exact prompt the AI would receive, so you can verify
				templates and module prompts before an event.
			</P>
			<OL>
				<li>
					Pick a <strong>Session</strong>, then a <strong>Module</strong> attached to
					it (leave blank to use the session&apos;s first module).
				</li>
				<li>
					Pick a <strong>Stage</strong> (Start, Interview, or Summary).
				</li>
				<li>
					Optionally enter an <strong>Interview ID</strong> to fill in a real
					participant&apos;s name, role, and transcript.
				</li>
				<li>Click <strong>Preview</strong>.</li>
			</OL>
			<UL>
				<li>
					The <strong>Rendered</strong> tab shows the final prompt with each
					placeholder highlighted — <strong>green</strong> means it resolved to a
					value, <strong>amber</strong> means it is still unresolved. Hover a
					highlight to see the placeholder name.
				</li>
				<li>
					The <strong>Pre-rendered</strong> tab shows the raw template with
					placeholders intact.
				</li>
			</UL>
		</Section>

		<Section id="conversations" title="Conversations" roles="Super Admin · Admin · App Admin (read-only)">
			<P>
				Conversations are the recorded interviews users have with the AI. This area is
				read-only.
			</P>
			<UL>
				<li>
					Filter the list by <strong>Group</strong> or by session (the{' '}
					<strong>Events</strong> filter).
				</li>
				<li>
					Open a row to see the participant details, the full{' '}
					<strong>Transcript</strong>, and the AI-generated <strong>Feedback</strong>{' '}
					on separate tabs.
				</li>
				<li>
					The per-row <strong>Export</strong> button downloads a single conversation as
					an HTML file including the transcript and feedback. The list-level{' '}
					<strong>Export</strong> downloads a CSV of participant details only —
					transcripts are deliberately excluded.
				</li>
			</UL>
		</Section>

		<Section id="groups" title="Groups" roles="Super Admin · Admin">
			<P>
				Groups tie users to sessions. When a session&apos;s visibility is set to{' '}
				<em>Specific Groups</em>, only members of its groups can join it.
			</P>
			<UL>
				<li>
					<strong>Name</strong> is required and must be unique — you will get an error
					if a group with that name already exists.
				</li>
				<li>
					Assign <strong>Users</strong> and <strong>Sessions</strong> directly on the
					group form. (You can also assign groups from the user form, or automatically
					via the user CSV import.)
				</li>
			</UL>
		</Section>

		<Section id="users" title="Users & Import" roles="Super Admin · Admin">
			<P>
				The Users list is searchable by name or email and shows each user&apos;s role,
				last login, and active status.
			</P>
			<H3>Creating and editing users</H3>
			<UL>
				<li>
					<strong>Email</strong> (required, unique) and <strong>Name</strong>{' '}
					(required).
				</li>
				<li>
					<strong>New password</strong> — on edit, leave blank to keep the current
					password; enter a value to replace it.
				</li>
				<li>
					<strong>Role</strong> — see the role table under Getting Started. You cannot
					change your own role or deactivate yourself, and only Super Admins can grant
					or edit Super Admin accounts.
				</li>
				<li>
					<strong>Active</strong> — deactivated users cannot log in anywhere.
				</li>
			</UL>
			<H3>Bulk import from CSV</H3>
			<P>
				Click <strong>Import</strong> in the list toolbar. Upload a CSV with a header
				row; rows are matched to existing users by email — matches are updated,
				everything else is created.
			</P>
			<RefTable
				head={['Column', 'Notes']}
				rows={[
					[<Code>email</Code>, 'Required. Unique key used to match existing users.'],
					[
						<>
							<Code>first name</Code> / <Code>last name</Code>
						</>,
						'Combined into the user’s name (a single "name" column also works).',
					],
					[
						<Code>password</Code>,
						'Optional. New users get a random password if blank (shown once in the results). Existing users keep theirs unless a value is supplied.',
					],
					[<Code>position</Code>, 'Optional free text.'],
					[
						<Code>role</Code>,
						'Optional: superadmin, admin, appadmin, eventhost, or user. Defaults to user.',
					],
					[
						<Code>status</Code>,
						'Optional: active or inactive. Blank leaves existing users unchanged; new users default to active.',
					],
					[
						<>
							<Code>group1</Code>, <Code>group2</Code>, …
						</>,
						'Optional. Each group column assigns the user to that group, creating the group if needed.',
					],
				]}
			/>
			<UL>
				<li>
					<strong>Preview first.</strong> The &quot;Preview only&quot; checkbox is on
					by default — it processes up to the first 25 rows and shows what{' '}
					<em>would</em> happen without saving anything. Uncheck it and click{' '}
					<strong>Import</strong> to apply.
				</li>
				<li>
					Each row is processed independently, so one bad row does not stop the rest —
					errors are listed per row in the results.
				</li>
				<li>
					Generated passwords appear in the results table <strong>only once</strong> —
					copy them before closing the dialog.
				</li>
			</UL>
		</Section>

		<Section id="ip-access" title="IP Access" roles="Super Admin · Admin">
			<P>
				IP Access controls which IP addresses can reach the CMS and the kiosk. The list
				shows manual rules, automatic blocks created by failed logins, and read-only
				entries from the server&apos;s environment allow-list.
			</P>
			<UL>
				<li>
					<strong>Address</strong> accepts an exact IP or a CIDR range, for example{' '}
					<Code>203.0.113.10</Code> or <Code>203.0.113.0/24</Code>.
				</li>
				<li>
					<strong>Action</strong> is either <strong>Permanent block</strong> or{' '}
					<strong>Permanent allow</strong>. Allow rules also exempt that address from
					automatic failed-login blocking.
				</li>
				<li>
					Rows with source <Code>auto</Code> are created by the system: repeated failed
					logins from one address (5 within 30 minutes by default) trigger a temporary
					block (30 minutes by default). A successful login clears that address&apos;s
					failure count, and expired blocks clean themselves up.
				</li>
				<li>
					Use the per-row <strong>Block</strong> / <strong>Allow</strong> buttons to
					convert any row into a permanent rule. Rows with source <Code>env</Code> come
					from server configuration and cannot be edited here.
				</li>
			</UL>
			<Alert severity="warning" sx={{ mb: 1 }}>
				Blocking applies to every request, including the login page — be careful not to
				block your own address.
			</Alert>
		</Section>

		<Section id="tips" title="Tips & Troubleshooting">
			<UL>
				<li>
					<strong>Deletes are immediate and permanent.</strong> There is no undo for
					deleting sessions, modules, groups, users, or conversations.
				</li>
				<li>
					<strong>Unsaved changes are guarded.</strong> Forms warn you before
					navigating away with unsaved edits, but only clicking <strong>Save</strong>{' '}
					persists them.
				</li>
				<li>
					<strong>&quot;Access denied&quot;</strong> means your role does not permit
					that action — ask a Super Admin or Admin if you need more access.
				</li>
				<li>
					<strong>A module isn&apos;t showing up for users?</strong> Check, in order:
					the module is globally Active, it is attached to the session and Active
					there, the session itself is Active, and (for group-restricted sessions) the
					user is in one of the session&apos;s groups.
				</li>
				<li>
					<strong>A conversation fails to start?</strong> Confirm the module has an{' '}
					<em>active</em> prompt version on every stage (Start, Interview, Summary).
				</li>
				<li>
					<strong>Locked out by IP?</strong> Automatic blocks expire on their own
					(default 30 minutes), or another admin can delete the block under IP Access.
				</li>
			</UL>
		</Section>
	</Box>
);

export default Help;
